import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { authService } from '@/services/auth.service'
import { toast } from '@/hooks/use-toast'
import AnimatedLogo from '@/components/AnimatedLogo'
import { getErrorMessage, getErrorCode } from '@/utils/error'
import { API_CONFIG } from '@/config/api.config'

const OAUTH_NO_RESET_CODE = 'OAUTH_USER_NO_PASSWORD_RESET'

export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  // Tela passa pro modo "use Microsoft" quando o back retorna OAUTH_USER_NO_PASSWORD_RESET.
  // Conscientemente NÃO disparamos toast nesse caso pra não competir com o alerta inline.
  const [oauthBlocked, setOauthBlocked] = useState(false)
  const [oauthMessage, setOauthMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email.trim()) {
      toast({
        title: 'E-mail obrigatório',
        description: 'Por favor, informe seu e-mail.',
        variant: 'destructive',
      })
      return
    }

    setIsLoading(true)

    try {
      const response = await authService.forgotPassword(email)

      setEmailSent(true)

      toast({
        title: 'E-mail enviado!',
        description: response.message,
      })
    } catch (error: unknown) {
      const code = getErrorCode(error)

      if (code === OAUTH_NO_RESET_CODE) {
        setOauthBlocked(true)
        setOauthMessage(
          getErrorMessage(
            error,
            'Esta conta usa login da Microsoft. Use o botão abaixo para entrar.'
          )
        )
        return
      }

      toast({
        title: 'Erro ao solicitar recuperação',
        description: getErrorMessage(error, 'Ocorreu um erro. Tente novamente.'),
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (oauthBlocked) setOauthBlocked(false)
  }

  const handleOutlookLogin = () => {
    window.location.href = `${API_CONFIG.baseURL}/auth/microsoft`
  }

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-primary/5">
        <Card className="w-full max-w-md glass-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <AnimatedLogo size="lg" />
            </div>
            <div className="flex justify-center mb-4">
              <CheckCircle2 className="w-16 h-16 text-green-500" />
            </div>
            <CardTitle className="text-2xl">E-mail Enviado!</CardTitle>
            <CardDescription>
              Se o e-mail informado existir em nossa base, você receberá instruções para redefinir
              sua senha.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-lg p-4 text-sm text-amber-800 dark:text-amber-200">
              <p className="font-medium mb-1">📧 Verifique sua caixa de entrada</p>
              <p>
                O link de recuperação expira em <strong>30 minutos</strong>.
              </p>
              <p className="mt-2 text-xs">
                Não recebeu? Verifique sua pasta de spam ou tente novamente.
              </p>
            </div>

            <Button variant="outline" className="w-full" onClick={() => setEmailSent(false)}>
              Enviar novamente
            </Button>

            <div className="pt-4 border-t">
              <Link
                to="/auth"
                className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar para o login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-primary/5">
      <Card className="w-full max-w-md glass-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <AnimatedLogo size="lg" />
          </div>
          <CardTitle className="text-2xl">Esqueceu sua senha?</CardTitle>
          <CardDescription>
            Sem problemas! Informe seu e-mail e enviaremos instruções para redefinir sua senha.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={handleEmailChange}
                  className="pl-10"
                  disabled={isLoading}
                  required
                />
              </div>
            </div>

            {oauthBlocked && (
              <Alert className="border-primary/30 bg-primary/5">
                <AlertTitle>Esta conta usa Microsoft 365</AlertTitle>
                <AlertDescription className="space-y-3">
                  <p>
                    {oauthMessage ||
                      'Contas vinculadas ao Microsoft 365 não usam senha aqui. Entre direto pelo botão da Microsoft.'}
                  </p>
                  <Button type="button" className="w-full" onClick={handleOutlookLogin}>
                    Entrar com Microsoft
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            <Button type="submit" className="w-full" disabled={isLoading || oauthBlocked}>
              {isLoading ? 'Enviando...' : 'Enviar Link de Recuperação'}
            </Button>

            <div className="pt-4 border-t">
              <Link
                to="/auth"
                className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar para o login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
